/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import PropTypes, { bool } from 'prop-types';
import { map, result, size, mapObject, constant, noop, find, clone, last, uniq, contains, chain, forEach, get } from 'underscore';
import { CommandButton, PrimaryButton } from '@fluentui/react';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getContainerFactoryStyles } from './style';
import {
  FilterOptionsGroupItem,
  FilterOptionsItem,
  Types,
  FilterOptionsGroupPreivew,
} from '../../index';
import {
  findTargetNodeAndAncestorsNodes,
  getNodesCountRecursively,
  multilineValueOutProcessor,
  keyExtractor,
  MTToUIValueOptionsMapper,
  isTreeData,
} from '../util';
import { DataModel } from '../filter-options-group-item/dataModel';

const defaultSelectedQueryIndex = 0;
const previewPageKey = 'Preview';
const operatorOptions = [
  { key: 'equal', text: '=' },
  { key: 'notequal', text: '≠' },
];

class FilterOptionsGroupUnstyled extends React.PureComponent {
  constructor(props) {
    super(props);
    this.dataModel = [];
    this.initState();
    this.currentDataModelKeyHash = '';
  }

  componentDidMount() {
    /**
     * the reason why setTimeout(() => {...}, 0) there is because we have to collect the data
     * triggered by componentDidMount of children component while this component's componentDidMount
     * is going to be triggered after that so using this settimeout can hack this issue
     */
    if (this.props.defaultSelectedDataSource !== null) {
      setTimeout(() => {
        // set the default value into dataModel
        this.props.defaultSelectedDataSource
          // if the defaultSelectedDataSource count exceed the maximun of filter count
          // just ignore the reset of them
          .slice(0, this.props.maximumFiltersCount)
          .map((item) => {
            const operatorObject = this.getOperatorObject(item.PresetOperator);
            if (item.ValueOptions === null) {
              return this.setDataModel(keyExtractor(item), {
                operator: operatorObject,
                value: item.PresetValue,
              });
            }
            const selectedItem = find(item.ValueOptions, valueOption => valueOption.Selected);

            // This holds the list of selected keys for multiselect.
            const keyArray = chain(item.ValueOptions)
              .where({ Selected: true })
              .pluck('Value')
              .value();

            return this.setDataModel(keyExtractor(item), {
              operator: operatorObject,
              operatorText: operatorObject.text,
              value: get(selectedItem, 'Value'),
              valueText: get(selectedItem, 'Text'),
              valueKeys: keyArray,
              valueOptions: item.ValueOptions, // for tree selection data
            });
          });
        // wait for treeview component trigger data collect
        setTimeout(() => this.OnApply(), 0);
      }, 0);
    }
  }

  onFilterOptionsItemDeletedHandler = () => {
    const { selectedDataSource } = this.state;
    const clonedSelectedDataSource = clone(selectedDataSource);
    const removedItem = clonedSelectedDataSource.pop();
    this.setState({
      selectedDataSource: clonedSelectedDataSource,
    });
    this.props.onRemoveFilterOptionsItemCallback(clonedSelectedDataSource, removedItem);
  }

  onAddNewQuery = (dataModel) => {
    if (dataModel.length > 0 && last(dataModel).isValid) {
      const { dataSource } = this.props;
      const { selectedDataSource } = this.state;
      const { selectableDataSource } =
        this.getSelectedDataSourceAndSelectableDataSource(
          dataSource,
          selectedDataSource.map(i => keyExtractor(i))
        );
      const clonedSelectedDataSource = clone(selectedDataSource);
      const clonedSelectableDataSource = clone(selectableDataSource
        .filter(x => !find(
          selectedDataSource,
          y => keyExtractor(y) === keyExtractor(x)
        )));
      const addItem = clonedSelectableDataSource.shift();
      clonedSelectedDataSource.push(addItem);
      this.setState({
        selectedDataSource: clonedSelectedDataSource,
      });

      this.props.onAddFilterOptionsItemCallback(dataModel, addItem);
    }
  }

  onDataModelChange = (dataModel) => {
    const latestHash = this.getDataModelKeyHash(dataModel);
    if (this.currentDataModelKeyHash !== latestHash) {
      const keys = dataModel.map(i => i.id);
      const selectingKey = last(keys);
      const selectedKeys = keys.slice(0, -1);
      this.setSelectedAndSelectableDataSource(selectedKeys, selectingKey);
      this.currentDataModelKeyHash = latestHash;
    }
  }

  getOperatorObject(objKey) {
    return find(operatorOptions, i => i.key === (objKey || 'equal'));
  }

  getDataModelKeyHash = dataModel => dataModel.reduce((total, current) => total + current.id, '')

  getSelectedDataSourceAndSelectableDataSource = (
    dataSource,
    selectedKeys
  ) => {
    const selectedDataSource = [];
    let selectableDataSource = [];

    /**
     * Depth First Search
     * when we find the target node, the selectableNodes are
     * all the children nodes of target node +
     * all the parent nodes siblings of target node
     */
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < selectedKeys.length; i++) {
      const currentSelectedKey = selectedKeys[i];
      const nodes = dataSource;
      // eslint-disable-next-line no-plusplus
      for (let j = 0; j < nodes.length; j++) {
        const currentNode = nodes[j];
        const {
          target,
          parents,
        } = findTargetNodeAndAncestorsNodes(currentNode, currentSelectedKey);

        if (target) {
          // all the children nodes of target node
          const targetChildren = target.Children || [];
          // all the parent nodes siblings of target node
          const targetParentNodesChildren = parents.length !== 0 ?
            parents.reduce((total, current) => total.concat(current.Children || []), []) : [];
          // found selected node
          selectedDataSource.push(target);
          // make the selectableDataSource
          selectableDataSource = selectableDataSource
            .concat(targetParentNodesChildren, targetChildren);
        } else {
          // if not found , then add this node into selectableDataSource
          selectableDataSource.push(currentNode);
        }
      }
    }

    // dedupe selectableDataSource by its Id(Text)
    selectableDataSource = uniq(selectableDataSource, 'Text');

    // filter out the parent nodes
    selectableDataSource = selectableDataSource
      .filter(i => !contains(selectedKeys, keyExtractor(i)));

    return {
      selectedDataSource,
      selectableDataSource,
    };
  }

  getSelectedAndSelectableDataSourceWhenSelecting = (selectedKeys, selectingKey = []) => {
    const { dataSource } = this.props;
    const {
      selectableDataSource,
    } = this.getSelectedDataSourceAndSelectableDataSource(
      dataSource,
      selectedKeys
    );

    const {
      selectedDataSource,
    } = this.getSelectedDataSourceAndSelectableDataSource(
      dataSource,
      selectedKeys.concat(selectingKey)
    );

    return {
      selectableDataSource, selectedDataSource,
    };
  }

  setSelectedAndSelectableDataSource = (selectedKeys, selectingKey) => {
    const { dataSource } = this.props;
    if (selectedKeys && selectedKeys.length !== 0) {
      const {
        selectedDataSource,
      } = this.getSelectedAndSelectableDataSourceWhenSelecting(selectedKeys, selectingKey);

      this.setState({
        selectedDataSource,
      });
    } else {
      this.setState({
        selectedDataSource: dataSource.filter(i => keyExtractor(i) === selectingKey),
      });
    }
  }

  setDataModel = (modelKey, data = {
    key: '', value: '', operator: '', keyText: '', valueText: '', operatorText: '', valueKeys: null,
  }) => {
    const {
      key,
      value,
      operator,
      keyText,
      valueText,
      operatorText,
      valueKeys,
      valueOptions,
    } = data;

    const models = this.dataModel;
    const modelToUpdate = find(models, model => model.id === modelKey);
    if (modelToUpdate) {
      if (key) {
        modelToUpdate.data.key = key;
      }
      if (value) {
        modelToUpdate.data.value = value;
      }
      if (operator) {
        modelToUpdate.data.operator = operator;
      }
      if (keyText) {
        modelToUpdate.data.keyText = keyText;
      }
      if (valueText) {
        modelToUpdate.data.valueText = valueText;
      }
      if (operatorText) {
        modelToUpdate.data.operatorText = operatorText;
      }
      if (valueKeys) {
        modelToUpdate.data.valueKeys = valueKeys;
      }
      if (valueOptions) {
        modelToUpdate.data.valueOptions = valueOptions;
      }

      modelToUpdate.trigger('Change', modelToUpdate.data);
      this.onDataModelChange(this.dataModel);
    }
  }

  // If multivalue get list, if single select, get key, otherwise get processed freetext.
  getValueOnApply = (data, type) => {
    if ((this.props.enableMultiSelect && !contains(this.props.blacklistMultiSelect, data.key))
      || type === Types.TreeSelection) {
      if (data.valueKeys.length > 0) {
        return data.valueKeys.join(', ');
      } else if (data.valueText) {
        return data.valueText;
      }
      return multilineValueOutProcessor(data.value);
    }

    return data.value;
  }

  getValueOptions = (data) => {
    if (isTreeData(data.ValueOptions)) {
      return data.ValueOptions;
    }
    return data.ValueOptions ? data.ValueOptions.map(MTToUIValueOptionsMapper) : null;
  }

  getFilterOptionsType = (data) => {
    const { ValueOptions } = data;
    if (isTreeData(ValueOptions)) {
      return Types.TreeSelection;
    }
    return ValueOptions ? Types.Selection : Types.FreeText;
  }

  getFiltersCount(dataModel) {
    return dataModel.reduce((total, current) => {
      if (current.type === Types.TreeSelection) {
        // eslint-disable-next-line no-param-reassign
        total += current.data.treeSelectionData.length;
      } else {
        // eslint-disable-next-line no-param-reassign
        total += 1;
      }
      return total;
    }, 0);
  }

  ValidateDataModel = () => {
    const validateOptions = {
      allowedFiltersCount: this.props.maximumFiltersCount -
        (this.state.selectedDataSource.length - 1),
    };
    this.dataModel.forEach(i => i.validate(validateOptions));
  }

  constructDataModel(dataModel) {
    const dataModelResult = dataModel.filter(m => m.type !== Types.TreeSelection);
    const treeSelectionModel = dataModel.filter(m => m.type === Types.TreeSelection);
    if (treeSelectionModel.length > 0) {
      treeSelectionModel.forEach((m) => {
        // eslint-disable-next-line no-param-reassign
        m.data.treeSelectionData = map(
          m.data.treeSelectionData,
          d => ({ operator: m.data.operator, ...d })
        );
        forEach(m.data.treeSelectionData, (data) => {
          dataModelResult.push(new DataModel({
            data,
          }));
        });
      });
    }
    return dataModelResult;
  }

  OnApply = () => {
    this.ValidateDataModel();
    const isValid = this.dataModel.reduce((total, current) => total && current.isValid, true);
    this.props.onApply({
      data: this.constructDataModel(this.dataModel),
      isValid,
      dataSource: this.props.dataSource,
    });
    if (isValid) {
      this.setState({
        isShowPreview: true,
        previewContent: this.dataModel.map(i => ({
          key: i.data.keyText,
          operator: (this.props.enableOperator && !contains(this.props.blacklistOperator, i.data.key)) ? i.data.operatorText : '=',
          value: this.getValueOnApply(i.data, i.type),
        })),
      });
    }
  };

  OnAddQuery = () => {
    this.ValidateDataModel();
    this.onAddNewQuery(this.dataModel);
  };

  dataCollector = ({ index, dataModel }) => {
    this.dataModel[index] = dataModel;
    this.onDataModelChange(this.dataModel);
    if (dataModel.type === Types.TreeSelection) {
      this.forceUpdate();
    }
  }

  clearDataModelItem = (index) => {
    this.dataModel.splice(index, 1);
    this.onDataModelChange(this.dataModel);
  }

  initState = () => {
    const { dataSource, defaultSelectedDataSource } = this.props;
    if (defaultSelectedDataSource) {
      const defaultSelectedDataSourceKeys = defaultSelectedDataSource
        .map(i => keyExtractor(i));
      const {
        selectedDataSource,
      } = this.getSelectedDataSourceAndSelectableDataSource(
        dataSource,
        defaultSelectedDataSourceKeys
      );

      this.state = {
        selectedDataSource,
        isShowPreview: true,
        previewContent: [
          { key: '', operator: '', value: '' },
        ],
      };
    } else {
      this.state = {
        selectedDataSource: [dataSource[defaultSelectedQueryIndex]],
        isShowPreview: false,
        previewContent: [
          { key: '', operator: '', value: '' },
        ],
      };
    }
  }

  isAbleToAddNewQuery = () => {
    const { selectedDataSource } = this.state;
    const { maximumFiltersCount } = this.props;
    const filtersCount = this.getFiltersCount(this.dataModel);
    return filtersCount < maximumFiltersCount &&
      selectedDataSource.length < this.totalNodesCountInDataSource;
  }

  isDeletable = (index) => {
    const { selectedDataSource } = this.state;
    return index === selectedDataSource.length - 1 && selectedDataSource.length > 1;
  }

  isDisabled = (index) => {
    const { selectedDataSource } = this.state;
    if (selectedDataSource.length === 1) {
      return false;
    }
    return index !== selectedDataSource.length - 1;
  }

  handleOnEdit = () => {
    this.setState({
      isShowPreview: false,
    });
  }

  renderGroupItems = () => this.state.selectedDataSource.map((data, index) => {
    const { Text, Value } = data;
    const { dataSource } = this.props;
    const { selectedDataSource } = this.state;

    const keys = map(selectedDataSource, i => i.Text);
    const selectableDataSource = size(keys) <= 1 ?
      dataSource.slice() :
      result(this.getSelectedAndSelectableDataSourceWhenSelecting(keys.slice(0, -1)), 'selectableDataSource', []);

    let keyOptions;
    if (find(selectableDataSource, i => keyExtractor(i) === Text)) {
      keyOptions = selectableDataSource.map(i => ({
        key: keyExtractor(i),
        text: i.Text,
      }));
    } else {
      keyOptions = [data].concat(selectableDataSource).map(i => ({
        key: keyExtractor(i),
        text: i.Text,
      }));
    }

    const valueOptions = this.getValueOptions(data);
    const deletable = this.isDeletable(index);
    const disabled = this.isDisabled(index);
    const mergedEnableOperator = this.props.enableOperator
      && !contains(this.props.blacklistOperator, Text);
    const mergedEnableMultiSelect = this.props.enableMultiSelect
      && !contains(this.props.blacklistMultiSelect, Text);
    const type = this.getFilterOptionsType(data);

    return (
      <FilterOptionsGroupItem
        key={Text}
        validationRules={[{
          rule: 'NotEmpty',
          errorMessage: this.props.i18nLegacy.getString('Filter_Validation_Common_Required'),
        },
        {
          rule: 'TotalLength',
          errorMessage: this.props.i18nLegacy.getString('Product_Condition_Validation_Common_Max_Length'),
        },
        {
          rule: 'SegmentLength',
          errorMessage: this.props.i18nLegacy.getString('Product_Condition_Validation_Common_Max_Segment'),
        },
        {
          rule: 'MaximumFiltersCount',
          errorMessage: this.props.i18nLegacy.getString('Product_Condition_Validation_Common_Max_Filters_Count', this.props.maximumFiltersCount),
        }]}
        dataCollector={this.dataCollector}
        onFilterOptionsItemDeleted={this.onFilterOptionsItemDeletedHandler}
        clearParentDataModel={this.clearDataModelItem}
        index={index}
        type={type}
      >
        <FilterOptionsItem
          i18nLegacy={this.props.i18nLegacy}
          type={type}
          itemKey={Text}
          key={Text}
          keyOptions={keyOptions}
          value={Value}
          valueOptions={valueOptions}
          operatorOptions={operatorOptions}
          deletable={deletable}
          disabled={disabled}
          keyOptionsDefaultSelectedKey={Text}
          enableOperator={mergedEnableOperator}
          enableMultiSelect={mergedEnableMultiSelect}
          blacklistOperator={this.props.blacklistOperator}
          blacklistMultiSelect={this.props.blacklistMultiSelect}
        />
      </FilterOptionsGroupItem>
    );
  })

  render() {
    const {
      classes: {
        Container, AddNewQuery, BottomButtonContainer, Invisible,
      },
      i18nLegacy,
      id,
    } = this.props;

    const {
      isShowPreview,
      previewContent,
    } = this.state;

    this.totalNodesCountInDataSource = this.props.dataSource
      .reduce((total, root) => total + getNodesCountRecursively(root), 0);

    return (
      <React.Fragment>
        <div className={isShowPreview ? Invisible : Container} id={id}>
          {
            this.renderGroupItems()
          }
          { this.isAbleToAddNewQuery() &&
          <div className={AddNewQuery}>
            <CommandButton
              id="filter_options_group_addQueryButton"
              iconProps={{ iconName: 'Add' }}
              text={i18nLegacy.getString('ShoppingCampaign_Add_AnotherQuery')}
              styles={{ padding: '0' }}
              onClick={this.OnAddQuery}
            />
          </div>
            }
          <div className={BottomButtonContainer}>
            <PrimaryButton id="filter_options_group_applyButton" text={i18nLegacy.getString('Apply')} onClick={this.OnApply} />
          </div>
        </div>
        <FilterOptionsGroupPreivew
          key={previewPageKey}
          onEdit={this.handleOnEdit}
          Content={previewContent}
          i18nLegacy={i18nLegacy}
          showPreview={isShowPreview}
        />
      </React.Fragment>
    );
  }
}

FilterOptionsGroupUnstyled.propTypes = {
  classes: PropTypes.shape(mapObject(
    getContainerFactoryStyles(),
    constant(PropTypes.string.isRequired)
  )).isRequired,
  dataSource: PropTypes.arrayOf(PropTypes.shape({
    Text: PropTypes.string.isRequired,
    Value: PropTypes.string.isRequired,
    ValueOptions: PropTypes.arrayOf(PropTypes.shape({
      Selected: PropTypes.bool.isRequired,
      Text: PropTypes.string.isRequired,
      Value: PropTypes.string.isRequired,
    })),
  })).isRequired,
  defaultSelectedDataSource: PropTypes.arrayOf(PropTypes.shape({
    Text: PropTypes.string.isRequired,
    Value: PropTypes.string.isRequired,
    PresetValue: PropTypes.string,
    PresetOperator: PropTypes.string,
    ValueOptions: PropTypes.arrayOf(PropTypes.shape({
      Selected: PropTypes.bool.isRequired,
      Text: PropTypes.string.isRequired,
      Value: PropTypes.string.isRequired,
    })),
  })),
  onApply: PropTypes.func,
  onAddFilterOptionsItemCallback: PropTypes.func,
  onRemoveFilterOptionsItemCallback: PropTypes.func,
  i18nLegacy: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  id: PropTypes.string,
  maximumFiltersCount: PropTypes.number,
  enableOperator: bool,
  enableMultiSelect: bool,
  blacklistOperator: PropTypes.arrayOf(PropTypes.string),
  blacklistMultiSelect: PropTypes.arrayOf(PropTypes.string),
};

FilterOptionsGroupUnstyled.defaultProps = {
  onApply: noop,
  onAddFilterOptionsItemCallback: noop,
  onRemoveFilterOptionsItemCallback: noop,
  defaultSelectedDataSource: null,
  id: 'FilterOptionsGroupContainer',
  maximumFiltersCount: 100,
  enableOperator: false,
  enableMultiSelect: false,
  blacklistOperator: [],
  blacklistMultiSelect: [],
};

const FilterOptionsGroup =
  withDefaultStyles(FilterOptionsGroupUnstyled, getContainerFactoryStyles);

export {
  FilterOptionsGroup
};

